import { type QueryKey, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import { useWxCopilotContext } from '@/components/pages/wx-copilot-host/context';
import { queryKeys } from '@/query-keys';

/** Collapse separators so camelCase and snake_case tool ids match the same fragments. */
function normalizeToolNameForQueryMatch(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]/g, '');
}

function toolCallNameMatchesQueryFragments(
  toolName: string | null,
  fragments: readonly string[],
): boolean {
  if (!toolName) return false;
  const t = normalizeToolNameForQueryMatch(toolName);
  return fragments.some(fragment =>
    t.includes(normalizeToolNameForQueryMatch(fragment)),
  );
}

type QueryInvalidationRule = {
  toolNameFragments: readonly string[];
  queryKeysToInvalidate: readonly QueryKey[];
};

/** Rules for invalidating React Query keys when a copilot tool is executed. */
/* for now we are keeping both the new tools and the old tools in the query invalidation rules. */
const QUERY_INVALIDATION_RULES: readonly QueryInvalidationRule[] = [
  {
    toolNameFragments: [
      'postInternalApiSourcingSourcingPurchasesBulkCreate',
      'putInternalApiSourcingSourcingPurchasesSourcingPurchaseId',
      'patchInternalApiSourcingSourcingPurchasesSourcingPurchaseIdUpdateCampaignStatus',
      'create_sourcing_campaigns',
      'update_budget_date_sourcing_campaign',
      'pause_resume_sourcing_campaign',
    ],
    queryKeysToInvalidate: [
      queryKeys.openingDetails.channels.list,
      queryKeys.openingDetails.detail,
      queryKeys.dashboard.openings,
      queryKeys.openingDetails.channels.campaignDetails,
      queryKeys.allCampaigns,
      queryKeys.campaignsMetrics,
    ],
  },
  {
    toolNameFragments: [
      'postToolsFunnels',
      'postInternalApiSourcingOpeningsOpeningSlug',
      'patchInternalApiSourcingDashboardOpeningSlug',
      'add_or_remove_openings_to_dashboard',
    ],
    queryKeysToInvalidate: [queryKeys.openingDetails.detail],
  },
  {
    toolNameFragments: [
      'patchInternalApiSourcingOpeningsOpeningSlugMapSourcingStages',
      'map_sourcing_funnel_stages_for_automation',
    ],
    queryKeysToInvalidate: [queryKeys.openingDetails.funnel.details],
  },
  {
    toolNameFragments: [
      'patchInternalApiSourcingDashboardOpeningSlug',
      'postToolsFunnels',
      'add_or_remove_openings_to_dashboard',
    ],
    queryKeysToInvalidate: [
      queryKeys.dashboard.openings,
      queryKeys.dashboard.analytics,
    ],
  },
  {
    toolNameFragments: [
      'patchInternalApiSourcingSourcingSettings',
      'update_automation_dashboard_settings',
    ],
    queryKeysToInvalidate: [
      queryKeys.settings.automations,
      queryKeys.openingDetails.detail,
    ],
  },
  {
    toolNameFragments: [
      'deleteInternalApiSourcingSourcingChannels',
      'postInternalApiSourcingSourcingChannelsEnableChannels',
      'activate_new_channel',
    ],
    queryKeysToInvalidate: [
      queryKeys.settings.channels.integrated,
      queryKeys.settings.channels.utmMapping,
    ],
  },
  {
    toolNameFragments: [
      'postInternalApiSourcingSourcingChannels',
      'create_update_channel_utm_mapping',
    ],
    queryKeysToInvalidate: [
      queryKeys.settings.channels.nonIntegrated,
      queryKeys.settings.channels.utmMapping,
    ],
  },
  {
    toolNameFragments: ['create_channel_contract'],
    queryKeysToInvalidate: [
      queryKeys.settings.contracts.vonqContracts,
      queryKeys.settings.contracts.vonqChannels,
    ],
  },
];

/** True when a completed Cue tool should be recorded for React Query invalidation. */
export function isCopilotMutationToolName(toolName: string | null): boolean {
  return QUERY_INVALIDATION_RULES.some(rule =>
    toolCallNameMatchesQueryFragments(toolName, rule.toolNameFragments),
  );
}

/**
 * Invalidates mapped query keys for a recorded copilot tool.
 * Subscribed from App so query-driven refetch works across routes.
 */
export function useCopilotQueryInvalidation(): void {
  const { lastToolCallName, clearLastToolCallName } = useWxCopilotContext();
  const queryClient = useQueryClient();

  useEffect(() => {
    const keysToInvalidate = QUERY_INVALIDATION_RULES.flatMap(rule =>
      toolCallNameMatchesQueryFragments(
        lastToolCallName,
        rule.toolNameFragments,
      )
        ? rule.queryKeysToInvalidate
        : [],
    );

    if (keysToInvalidate.length === 0) {
      return;
    }

    void Promise.all(
      keysToInvalidate.map(queryKey =>
        queryClient.invalidateQueries({ queryKey }),
      ),
    ).finally(() => {
      clearLastToolCallName();
    });
  }, [lastToolCallName, queryClient, clearLastToolCallName]);
}
